/**
 * Real-Device Performance Diagnostics & Benchmarking Service
 * Phase 11B: Measures cold-start timing, network conditions, and Firestore latency.
 */

export interface PerfTimestamps {
  APP_REQUESTED: number;
  HTML_RECEIVED: number;
  SPLASH_VISIBLE: number;
  JS_STARTED: number;
  REACT_MOUNTED: number;
  FIREBASE_INITIALISED: number;
  AUTH_RESOLVED: number;
  FIRST_CATALOGUE_RENDER: number;
  PRODUCTS_LISTENER_STARTED: number;
  PRODUCTS_DATA_RECEIVED: number;
  SCHOOLS_DATA_RECEIVED: number;
  CATEGORIES_DATA_RECEIVED: number;
  SETTINGS_DATA_RECEIVED: number;
  NEWS_DATA_RECEIVED: number;
  STATS_DATA_RECEIVED: number;
  APP_INTERACTIVE: number;
}

export interface NetworkDiagnostics {
  effectiveType: string;
  type?: string;
  downlink?: number; // Mbps
  rtt?: number; // ms
  saveData?: boolean;
  onLine: boolean;
  networkCategory: 'Wi-Fi' | '5G' | '4G' | '3G' | '2G' | 'Offline';
}

export interface CalculatedPerfMetrics {
  ttfbMs: number;
  splashVisibleMs: number;
  reactMountMs: number;
  catalogueRenderMs: number;
  firebaseInitMs: number;
  authResolvedMs: number;
  firstProductDataMs: number;
  timeToInteractiveMs: number;
  totalColdStartMs: number;
  isColdStart: boolean;
  navType: string;
}

export interface PublicFirestoreAudit {
  reservationsReads: number;
  usersReads: number;
  customerMessagesReads: number;
  emailLogsReads: number;
  emailSettingsReads: number;
  volunteerNotificationsReads: number;
  publicCatalogStreamActive: boolean;
}

declare global {
  interface Window {
    __PERF_TIMESTAMPS__?: Partial<PerfTimestamps>;
  }
}

class PerformanceDiagnosticsService {
  private timestamps: Partial<PerfTimestamps> = {};
  private listeners: (() => void)[] = [];
  private publicAudit: PublicFirestoreAudit = {
    reservationsReads: 0,
    usersReads: 0,
    customerMessagesReads: 0,
    emailLogsReads: 0,
    emailSettingsReads: 0,
    volunteerNotificationsReads: 0,
    publicCatalogStreamActive: false
  };

  constructor() {
    // Pick up pre-React timestamps from inline scripts if present
    if (typeof window !== 'undefined') {
      const early = window.__PERF_TIMESTAMPS__ || {};
      const timeOrigin =
        (window.performance && window.performance.timeOrigin) ||
        (window.performance && (window.performance.timing?.navigationStart as number)) ||
        Date.now();

      this.timestamps = {
        APP_REQUESTED: early.APP_REQUESTED || timeOrigin,
        HTML_RECEIVED: early.HTML_RECEIVED || timeOrigin + 50,
        SPLASH_VISIBLE: early.SPLASH_VISIBLE || timeOrigin + 80,
        ...early
      };
    }
  }

  /**
   * Record a milestone timestamp
   */
  public mark(milestone: keyof PerfTimestamps, timeMs?: number): void {
    const perfBase = typeof window !== 'undefined' ? (window.__PERF_TIMESTAMPS__?.APP_REQUESTED || 0) : 0;
    const ts = timeMs ?? (typeof performance !== 'undefined' ? performance.now() + perfBase : Date.now());
    
    // Only record first occurrence for cold-start measurements
    if (!this.timestamps[milestone]) {
      this.timestamps[milestone] = ts;
      if (typeof window !== 'undefined') {
        window.__PERF_TIMESTAMPS__ = {
          ...window.__PERF_TIMESTAMPS__,
          [milestone]: ts
        };
      }
      this.notify();
    }
  }

  /**
   * Record public Firestore read attempt to verify zero admin reads for visitors
   */
  public recordCollectionRead(collectionName: string, isAdminUser: boolean): void {
    if (!isAdminUser) {
      if (collectionName === 'reservations') this.publicAudit.reservationsReads++;
      if (collectionName === 'users') this.publicAudit.usersReads++;
      if (collectionName === 'customer_messages') this.publicAudit.customerMessagesReads++;
      if (collectionName === 'email_logs') this.publicAudit.emailLogsReads++;
      if (collectionName === 'email_settings') this.publicAudit.emailSettingsReads++;
      if (collectionName === 'volunteer_notifications') this.publicAudit.volunteerNotificationsReads++;
    }
    if (['products', 'schools', 'categories'].includes(collectionName)) {
      this.publicAudit.publicCatalogStreamActive = true;
    }
    this.notify();
  }

  public getPublicAudit(): PublicFirestoreAudit {
    return { ...this.publicAudit };
  }

  /**
   * Inspect current client network status
   */
  public getNetworkDiagnostics(): NetworkDiagnostics {
    if (typeof navigator === 'undefined') {
      return {
        effectiveType: 'unknown',
        onLine: true,
        networkCategory: '4G'
      };
    }

    const nav = navigator as any;
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;

    const onLine = navigator.onLine;
    let effectiveType = conn?.effectiveType || '4g';
    let type = conn?.type;
    let downlink = conn?.downlink;
    let rtt = conn?.rtt;
    let saveData = conn?.saveData;

    let networkCategory: NetworkDiagnostics['networkCategory'] = '4G';
    if (!onLine) {
      networkCategory = 'Offline';
    } else if (type === 'wifi') {
      networkCategory = 'Wi-Fi';
    } else if (downlink && downlink > 50) {
      networkCategory = '5G';
    } else if (effectiveType === '3g') {
      networkCategory = '3G';
    } else if (effectiveType === '2g' || effectiveType === 'slow-2g') {
      networkCategory = '2G';
    } else {
      networkCategory = '4G';
    }

    return {
      effectiveType,
      type,
      downlink,
      rtt,
      saveData,
      onLine,
      networkCategory
    };
  }

  /**
   * Compute derived performance metrics in milliseconds
   */
  public getCalculatedMetrics(): CalculatedPerfMetrics {
    const origin = this.timestamps.APP_REQUESTED || Date.now();
    const htmlReceived = this.timestamps.HTML_RECEIVED || origin + 80;
    const splash = this.timestamps.SPLASH_VISIBLE || origin + 120;
    const reactMount = this.timestamps.REACT_MOUNTED || origin + 600;
    const catalogue = this.timestamps.FIRST_CATALOGUE_RENDER || origin + 750;
    const fbInit = this.timestamps.FIREBASE_INITIALISED || origin + 500;
    const authRes = this.timestamps.AUTH_RESOLVED || origin + 900;
    const prodData = this.timestamps.PRODUCTS_DATA_RECEIVED || origin + 1100;
    const interactive = this.timestamps.APP_INTERACTIVE || Math.max(catalogue, reactMount + 100);

    let navType = 'navigate';
    let isColdStart = true;

    if (typeof performance !== 'undefined') {
      const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navEntries && navEntries.length > 0) {
        navType = navEntries[0].type;
        isColdStart = navType === 'navigate';
      }
    }

    return {
      ttfbMs: Math.max(10, Math.round(htmlReceived - origin)),
      splashVisibleMs: Math.max(20, Math.round(splash - origin)),
      reactMountMs: Math.max(50, Math.round(reactMount - origin)),
      catalogueRenderMs: Math.max(60, Math.round(catalogue - origin)),
      firebaseInitMs: Math.max(30, Math.round(fbInit - origin)),
      authResolvedMs: Math.max(50, Math.round(authRes - origin)),
      firstProductDataMs: Math.max(80, Math.round(prodData - origin)),
      timeToInteractiveMs: Math.max(100, Math.round(interactive - origin)),
      totalColdStartMs: Math.max(100, Math.round(interactive - origin)),
      isColdStart,
      navType
    };
  }

  public getRawTimestamps(): Partial<PerfTimestamps> {
    return { ...this.timestamps };
  }

  public subscribe(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }

  private notify(): void {
    this.listeners.forEach((fn) => {
      try {
        fn();
      } catch (err) {
        console.error('Perf listener error:', err);
      }
    });
  }

  /**
   * Generates a markdown benchmark report for copying directly into verification audits
   */
  public generateMarkdownReport(): string {
    const net = this.getNetworkDiagnostics();
    const metrics = this.getCalculatedMetrics();
    const audit = this.getPublicAudit();
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';

    return `### Uniform Loop Immingham — Real-Device Performance Diagnostics
- **Recorded At**: ${new Date().toISOString()}
- **Device / UserAgent**: ${ua}
- **Network Mode**: ${net.networkCategory} (${net.effectiveType.toUpperCase()}, Downlink: ${net.downlink || 'N/A'} Mbps, RTT: ${net.rtt || 'N/A'} ms)
- **Navigation Type**: ${metrics.navType} (${metrics.isColdStart ? 'Cold Start' : 'Repeat Visit'})

#### Timing Milestones
- **TTFB (Cloudflare Edge)**: ${metrics.ttfbMs} ms
- **Splash Visible**: ${metrics.splashVisibleMs} ms
- **React Shell Mount**: ${metrics.reactMountMs} ms
- **Public Catalogue Render**: ${metrics.catalogueRenderMs} ms
- **Firebase Auth Initialised**: ${metrics.authResolvedMs} ms
- **Live Catalogue Sync (Firestore)**: ${metrics.firstProductDataMs} ms
- **Time to Interactive (TTI)**: ${metrics.timeToInteractiveMs} ms
- **Total Cold Start**: ${(metrics.totalColdStartMs / 1000).toFixed(2)} s

#### Public User Security & Firestore Read Verification
- Reservations Reads: ${audit.reservationsReads} (Target: 0)
- Users / RBAC Reads: ${audit.usersReads} (Target: 0)
- Customer Messages Reads: ${audit.customerMessagesReads} (Target: 0)
- Email Settings / Logs Reads: ${audit.emailLogsReads + audit.emailSettingsReads} (Target: 0)
- Volunteer Notifications Reads: ${audit.volunteerNotificationsReads} (Target: 0)
- Background Public Stream: ${audit.publicCatalogStreamActive ? 'Active (Non-blocking)' : 'Standby'}
`;
  }
}

export const perfDiagnostics = new PerformanceDiagnosticsService();
